import React from "react";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import type { TrackingData } from "../../hooks/useEmailTracking";

/**
 * Derives the badge tier from raw tracking data.
 *
 * Tier hierarchy (highest priority first):
 *   Clicked    (green)  — non-bot link click. Definitive human engagement.
 *   Opened     (green)  — multiple pixel fires >2 min apart. Real human came back.
 *   Delivered  (amber)  — pixel fired at least once. Reached an image-loading client,
 *                         but single fire can't distinguish real open from Gmail/Apple
 *                         pre-fetch. Honestly labels the ambiguity.
 *   Sent       (gray)   — API confirmed send but no pixel fire yet, <24h old.
 *   Not opened (red)    — no pixel fire after 24h. Likely spam-filtered or ignored.
 */
function deriveBadge(data: TrackingData): {
  label: string;
  color: "success" | "warning" | "default" | "error";
  tooltip: string;
} | null {
  const nonBotClicks = data.clicks.filter((c) => !c.suspected_bot);

  // Tier 1: Clicked (green) — any non-bot click
  if (nonBotClicks.length > 0) {
    const firstClick = nonBotClicks[0];
    const linkLabel = firstClick.link_label
      ? ` "${firstClick.link_label}"`
      : "";
    return {
      label: "Clicked",
      color: "success",
      tooltip: `Clicked${linkLabel} ${formatRelativeTime(firstClick.clicked_at)}`,
    };
  }

  // Tier 2: Opened (green) — STRONG signal of real human
  // Multiple pixel fires across time (>2 min apart) means the recipient came
  // back to the email — not just a one-time pre-fetch by Gmail/Apple.
  if (data.open_count > 1 && data.first_opened_at && data.last_opened_at) {
    const first = new Date(data.first_opened_at).getTime();
    const last = new Date(data.last_opened_at).getTime();
    if (last - first > 120_000) {
      return {
        label: "Opened",
        color: "success",
        tooltip: `Opened ${data.open_count} times, last ${formatRelativeTime(data.last_opened_at)}`,
      };
    }
  }

  // Tier 3: Delivered (amber) — pixel fired but ambiguous
  // One fire (or multiple within 2 min) can't distinguish a real open from
  // Gmail's image proxy pre-fetch or Apple Mail Privacy Protection. Honest
  // label: "it reached a client that loads images."
  if (data.open_count >= 1) {
    return {
      label: "Delivered",
      color: "warning",
      tooltip: `Email reached the recipient ${formatRelativeTime(data.first_opened_at)}. Real open not confirmed — could be email client pre-fetching images.`,
    };
  }

  // Tier 4/5: Sent vs Not opened — based on time since send
  if (data.sent_at) {
    const hoursSinceSend =
      (Date.now() - new Date(data.sent_at).getTime()) / (1000 * 60 * 60);

    if (hoursSinceSend < 24) {
      return {
        label: "Sent",
        color: "default",
        tooltip: `Sent ${formatRelativeTime(data.sent_at)}`,
      };
    }

    return {
      label: "Not opened",
      color: "error",
      tooltip: `Sent ${formatRelativeTime(data.sent_at)}, no delivery confirmation`,
    };
  }

  return null;
}

function formatRelativeTime(isoDate?: string): string {
  if (!isoDate) return "";
  const diff = Date.now() - new Date(isoDate).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  return new Date(isoDate).toLocaleDateString();
}

interface EmailTrackingBadgeProps {
  trackingData?: TrackingData;
}

export default function EmailTrackingBadge({
  trackingData,
}: EmailTrackingBadgeProps) {
  // No tracking data = pre-feature email, show nothing
  if (!trackingData) return null;

  const badge = deriveBadge(trackingData);
  if (!badge) return null;

  return (
    <Tooltip title={badge.tooltip} arrow>
      <Chip
        label={badge.label}
        color={badge.color}
        size="small"
        variant="outlined"
        sx={{ ml: 1, height: 22, fontSize: "0.75rem" }}
      />
    </Tooltip>
  );
}
